import { ApplicationConfig, LOCALE_ID, ModuleWithProviders, Provider, importProvidersFrom } from '@angular/core';
import { InMemoryScrollingFeature, InMemoryScrollingOptions, provideRouter, withInMemoryScrolling } from '@angular/router';
import { routes } from './shared/routes/routes';
import { NoopAnimationsModule, provideAnimations } from '@angular/platform-browser/animations';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ApiModule as ApiAuthModule, Configuration as ApiAuthConfiguration, ConfigurationParameters as ApiAuthConfigurationParameters } from 'api-auth';
import { ApiModule as ApiClippingModule, Configuration as ApiClippingConfiguration, ConfigurationParameters as ApiClippingConfigurationParameters } from 'api-clipping';
import { ApiModule as ApiClientModule, Configuration as  ApiClientConfiguration, ConfigurationParameters as  ApiClientConfigurationParameters } from 'api-client';
import { ApiModule as ApiAiHubModule, Configuration as  ApiAiHubConfiguration, ConfigurationParameters as  ApiAiHubConfigurationParameters } from 'api-ai-hub';
import { ApiModule as ApiContractNoticesModule, Configuration as  ApiContractNoticesConfiguration, ConfigurationParameters as  ApiContractNoticesConfigurationParameters } from 'api-contract-notices';
import { ApiModule as ApiNotificationModule, Configuration as  ApiNotificationConfiguration, ConfigurationParameters as  ApiNotificationConfigurationParameters } from 'api-notification';
import { ApiModule as ApiTranscriptionModule, Configuration as  ApiTranscriptionConfiguration, ConfigurationParameters as  ApiTranscriptionConfigurationParameters } from 'api-transcription';

import { environment } from '../environments/environment';
import { mainInterceptor } from './shared/interceptors/main.interceptor';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions, MatTooltipModule } from '@angular/material/tooltip';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions, MatFormFieldModule } from '@angular/material/form-field';
import { MAT_PROGRESS_SPINNER_DEFAULT_OPTIONS, MatProgressSpinnerDefaultOptions, MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { IMAGE_CONFIG } from '@angular/common';
// import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats } from '@angular/material/core';

export function createTranslateLoader(http: HttpClient) {
	return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function apiAuthFactory(): ApiAuthConfiguration {
  const params: ApiAuthConfigurationParameters = {
    basePath: environment.apiAuthUrl
  };
  return new ApiAuthConfiguration(params);
}

export function apiClippingFactory(): ApiClippingConfiguration {
  const params: ApiClippingConfigurationParameters = {
    basePath: environment.apiClippingUrl
  };
  return new ApiClippingConfiguration(params);
}

export function apiClientFactory(): ApiClientConfiguration {
  const params: ApiClientConfigurationParameters = {
    basePath: environment.apiClientUrl
  };
  return new ApiClientConfiguration(params);
}

export function apiAiHubFactory(): ApiAiHubConfiguration {
  const params: ApiAiHubConfigurationParameters = {
    basePath: environment.apiAiHubUrl
  };
  return new ApiAiHubConfiguration(params);
}

export function apiContractNoticesFactory(): ApiContractNoticesConfiguration {
  const params:  ApiContractNoticesConfigurationParameters = {
    basePath: environment.apiContractNotices
  };
  return new ApiContractNoticesConfiguration(params);
}

export function apiNotificationFactory(): ApiNotificationConfiguration {
  const params:  ApiNotificationConfigurationParameters = {
    basePath: environment.apiNotification
  };
  return new ApiNotificationConfiguration(params);
}

export function apiTranscriptionFactory(): ApiTranscriptionConfiguration {
  const params:  ApiTranscriptionConfigurationParameters = {
    basePath: environment.apiTranscription
  };
  return new ApiTranscriptionConfiguration(params);
}

export const customTooltipDefaults: MatTooltipDefaultOptions = {
  showDelay: 600,
  hideDelay: 0,
  touchendHideDelay: 0,
};

export const customFormFieldDefaults: MatFormFieldDefaultOptions = {
  appearance: 'outline',
  subscriptSizing: 'dynamic'
};

export const customMatSpinnerDefaults: MatProgressSpinnerDefaultOptions = {
  _forceAnimations: true,
}

// export const customMatDateFormat: MatDateFormats = {
//   parse: {
//     dateInput: 'DD/MM/YYYY', // this is how your date will be parsed from Input
//   },
//   display: {
//     dateInput: 'DD/MM/YYYY', // this is how your date will get displayed on the Input
//     monthYearLabel: 'MMMM YYYY',
//     dateA11yLabel: 'LL',
//     monthYearA11yLabel: 'MMMM YYYY'
//   }
// };

export class MatTooltipCustomConfModule {
  static forRoot(config: MatTooltipDefaultOptions): ModuleWithProviders<MatTooltipModule> {
    return {
      ngModule: MatTooltipModule,
      providers: [
        {provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: config }
      ]
    };
  }
}

export class MatFormFieldCustomConfModule {
  static forRoot(config: MatFormFieldDefaultOptions): ModuleWithProviders<MatFormFieldModule> {
    return {
      ngModule: MatFormFieldModule,
      providers: [
        {provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: config }
      ]
    };
  }
}

export class MatSpinnerCustomConfModule {
  static forRoot(config: MatProgressSpinnerDefaultOptions): ModuleWithProviders<MatProgressSpinnerModule> {
    return {
      ngModule: MatProgressSpinnerModule,
      providers: [
        {provide: MAT_PROGRESS_SPINNER_DEFAULT_OPTIONS,useValue: config}
      ]
  };
}
}

export function provideLocaleConfig(): Provider[] {
  return [
    {
      provide: LOCALE_ID,
      useValue: 'en-GB',
    },
    // Admin-wide date format, ДД.ММ.ГГГГ: de-DE is the one common Intl locale that renders a plain
    // Date this way out of the box (day.month.year with dots) - no new date-adapter library
    // needed, every existing mat-datepicker field picks this up automatically since none of them
    // sets its own MAT_DATE_LOCALE. Scoped to Material's date adapter only (not LOCALE_ID above,
    // which other, non-Material i18n - the `date` pipe, currency - still relies on as en-GB).
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'de-DE',
    },
    // {
    //   provide: MAT_DATE_FORMATS,
    //   useValue: customMatDateFormat,
    // },
    // {
    //   provide: DateAdapter,
    //   useClass: MomentDateAdapter,
    //   deps: [MAT_DATE_LOCALE],
    // }
  ];
}

const scrollConfig: InMemoryScrollingOptions = {
  scrollPositionRestoration: 'top',
  anchorScrolling: 'enabled',
};
const inMemoryScrollingFeature: InMemoryScrollingFeature =
  withInMemoryScrolling(scrollConfig);

export const appConfig: ApplicationConfig = {
	providers: [
		provideRouter(routes, inMemoryScrollingFeature),
		provideAnimations(),
    provideHttpClient(withInterceptors([mainInterceptor])),
		//importProvidersFrom(HttpClientModule),
		importProvidersFrom(ApiAuthModule.forRoot(apiAuthFactory)),
		importProvidersFrom(ApiClippingModule.forRoot(apiClippingFactory)),
		importProvidersFrom(ApiClientModule.forRoot(apiClientFactory)),
		importProvidersFrom(ApiAiHubModule.forRoot(apiAiHubFactory)),
		importProvidersFrom(ApiContractNoticesModule.forRoot(apiContractNoticesFactory)),
    importProvidersFrom(ApiNotificationModule.forRoot(apiNotificationFactory)),
    importProvidersFrom(ApiTranscriptionModule.forRoot(apiTranscriptionFactory)),
    importProvidersFrom(NoopAnimationsModule),
    importProvidersFrom(MatTooltipCustomConfModule.forRoot(customTooltipDefaults)),
    importProvidersFrom(MatFormFieldCustomConfModule.forRoot(customFormFieldDefaults)),
    importProvidersFrom(MatSpinnerCustomConfModule.forRoot(customMatSpinnerDefaults)),
    provideLocaleConfig(),
    importProvidersFrom(MatNativeDateModule),
		importProvidersFrom(
			TranslateModule.forRoot({
        defaultLanguage: localStorage.getItem('language') || 'mk',
				loader: {
					provide: TranslateLoader,
					useFactory: createTranslateLoader,
					deps: [HttpClient]
				},
			}),
		),
    { provide: IMAGE_CONFIG, useValue: { disableImageSizeWarning: true, disableImageLazyLoadWarning: true } }
	],
};
